import React from "react";
import { Link } from "react-router-dom";
import { useSettings } from "../lib/SettingsContext";
import { normalizePhone } from "../lib/whatsapp";
import { MessageCircle } from "lucide-react";

export default function Footer() {
  const { settings } = useSettings();
  const accent = settings?.brand_accent || "#FF3B30";
  const brandName = settings?.brand_name || "PRINTLAB";
  const logoHeight = Math.max(24, Math.min(96, Number(settings?.logo_height) || 32));

  const waPhone = normalizePhone(settings?.company_whatsapp);
  const waText = encodeURIComponent(`Hola ${brandName}, quisiera cotizar un pedido.`);
  const waHref = waPhone ? `https://wa.me/${waPhone}?text=${waText}` : null;

  return (
    <footer className="border-t border-neutral-200 bg-white mt-24">
      {/* WhatsApp CTA band */}
      {waHref && (
        <div className="border-b border-neutral-200 bg-emerald-50">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <div className="text-xs font-mono uppercase text-emerald-700 mb-1">¿Necesitas ayuda?</div>
              <div className="font-display text-2xl md:text-3xl tracking-tighter text-neutral-900">
                Escríbenos por WhatsApp
              </div>
              <p className="text-sm text-neutral-600 mt-1">Cotizaciones, dudas y seguimiento — respuesta rápida en horario de oficina.</p>
            </div>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium tracking-tight shadow-sm transition-colors"
              data-testid="footer-whatsapp-cta"
            >
              <MessageCircle className="w-5 h-5" />
              <span>Chatear ahora</span>
              {settings?.company_whatsapp && (
                <span className="hidden md:inline font-mono text-xs opacity-90 ml-1">{settings.company_whatsapp}</span>
              )}
            </a>
          </div>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2">
            {settings?.logo_url ? (
              <img
                src={settings.logo_url}
                alt={brandName}
                className="w-auto object-contain"
                style={{ height: `${logoHeight}px`, maxHeight: "96px", maxWidth: "260px" }}
              />
            ) : (
              <div className="font-display tracking-tighter" style={{ fontSize: `${logoHeight * 0.75}px` }}>
                {brandName}<span style={{ color: accent }}>.</span>
              </div>
            )}
          </div>
          <p className="text-sm text-neutral-500 mt-3 max-w-xs">
            {settings?.hero_subheadline || "Estudio de impresión personalizada."}
          </p>
        </div>
        <div>
          <div className="text-xs font-mono uppercase text-neutral-500 mb-3">Tienda</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/tienda?cat=playera" className="hover:text-black">Playeras</Link></li>
            <li><Link to="/tienda?cat=taza" className="hover:text-black">Tazas</Link></li>
            <li><Link to="/tienda?cat=termo" className="hover:text-black">Termos</Link></li>
            <li><Link to="/tienda?cat=camisa" className="hover:text-black">Camisas</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-mono uppercase text-neutral-500 mb-3">Empresa</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/como-funciona" className="hover:text-black">Cómo funciona</Link></li>
            <li><Link to="/contacto" className="hover:text-black">Contacto</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-mono uppercase text-neutral-500 mb-3">Contacto</div>
          <ul className="space-y-2 text-sm text-neutral-700">
            {settings?.company_email && <li>{settings.company_email}</li>}
            {settings?.company_phone && <li>{settings.company_phone}</li>}
            {settings?.company_whatsapp && waHref && (
              <li>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 font-medium"
                  data-testid="footer-whatsapp-link"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  {settings.company_whatsapp}
                </a>
              </li>
            )}
            {settings?.company_address && <li>{settings.company_address}</li>}
          </ul>
        </div>
      </div>
      <div className="border-t border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between text-xs text-neutral-500">
          <span>© {new Date().getFullYear()} {settings?.company_name || brandName}</span>
          <span className="font-mono">v1.0 · MX</span>
        </div>
      </div>
    </footer>
  );
}
